import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import TeamManagement from '../components/TeamManagement';
import InviteManagement from '../components/InviteManagement';
import BudgetSettings from '../components/BudgetSettings';
import NotificationSettings from '../components/NotificationSettings';
import BillingSettings from '../components/BillingSettings';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Cloud, CheckCircle, XCircle, AlertCircle, User, Shield, Users, UserPlus, Crown, DollarSign, Download, Bell, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SettingsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');

  useEffect(() => {
    fetchUser();
    checkDriveStatus();
    handleOAuthCallback();
    
    // Check for billing tab from URL
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'billing') {
      setActiveSettingsTab('billing');
    }
  }, []);

  const handleOAuthCallback = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state === 'settings_drive') {
      setIsConnecting(true);
      try {
        const REDIRECT_URI = `${window.location.origin}/drive-callback`;
        await axios.post(
          `${API}/auth/google-drive-callback`,
          { code, redirect_uri: REDIRECT_URI },
          { withCredentials: true }
        );
        
        setDriveConnected(true);
        toast.success('Google Drive connected successfully!');
        
        // Clean URL
        window.history.replaceState({}, document.title, '/settings');
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error(error.response?.data?.detail || 'Failed to connect Google Drive');
      } finally {
        setIsConnecting(false);
      }
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/login');
    }
  };

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/drive-status`, { withCredentials: true });
      setDriveConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check Drive status:', error);
    }
  };

  const connectDrive = () => {
    setIsConnecting(true);
    
    // Build OAuth URL for redirect flow
    const GOOGLE_CLIENT_ID = '1076707496906-ghdqm3gkbt00kq9pj9h5vjn8ncj8r8qh.apps.googleusercontent.com';
    const REDIRECT_URI = `${window.location.origin}/drive-callback`;
    const scope = 'https://www.googleapis.com/auth/drive.file';
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    
    authUrl.searchParams.append('client_id', GOOGLE_CLIENT_ID);
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('scope', scope);
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('state', 'settings_drive');
    authUrl.searchParams.append('prompt', 'consent');

    // Redirect to Google OAuth
    window.location.href = authUrl.toString();
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive? You can reconnect anytime.')) {
      return;
    }

    try {
      await axios.post(`${API}/auth/disconnect-drive`, {}, { withCredentials: true });
      setDriveConnected(false);
      toast.success('Google Drive disconnected');
    } catch (error) {
      console.error('Failed to disconnect Drive:', error);
      toast.error('Failed to disconnect Google Drive');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mb-2">
              Settings
            </h1>
            <p className="text-sm text-slate-600">
              Manage your account and app preferences
            </p>
          </div>

          {/* Account Section */}
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <User className="w-5 h-5 text-indigo-700 dark:text-indigo-400" />
              <h2 className="text-xl font-heading font-semibold text-slate-900 dark:text-white">Account</h2>
            </div>
            
            {user && (
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                      <User className="w-8 h-8 text-indigo-700 dark:text-indigo-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white flex items-center space-x-2">
                      <span>{user.name}</span>
                      {user.is_admin && (
                        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full flex items-center space-x-1">
                          <Crown className="w-3 h-3" />
                          <span>Admin</span>
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{user.email}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Settings Tabs */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setActiveSettingsTab('general')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  activeSettingsTab === 'general'
                    ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                General
              </button>
              <button
                onClick={() => setActiveSettingsTab('billing')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-2 ${
                  activeSettingsTab === 'billing'
                    ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
                data-testid="billing-tab"
              >
                <CreditCard className="w-4 h-4" />
                Billing
              </button>
            </div>
          </div>

          {activeSettingsTab === 'billing' ? (
            <BillingSettings />
          ) : (
            <>
              {/* Invite Management Section (Admin Only) */}
          {user?.is_admin && (
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <UserPlus className="w-5 h-5 text-indigo-700" />
                <h2 className="text-xl font-heading font-semibold text-slate-900">User Management</h2>
              </div>
              
              <p className="text-sm text-slate-600 mb-4">
                Invite new members by email. Only invited emails can access this app.
              </p>

              <InviteManagement currentUser={user} />
            </div>
          )}

          {/* Budget & Preferences Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <DollarSign className="w-5 h-5 text-indigo-700" />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Budget & Preferences</h2>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Set monthly budget limits, alerts, and customize your experience
            </p>

            <BudgetSettings />
          </div>

          {/* Notification Settings Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
            <NotificationSettings />
          </div>

          {/* Export Formats Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Download className="w-5 h-5 text-indigo-700" />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Accounting Exports</h2>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Export your invoices in formats compatible with accounting software
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={`${API}/export/quickbooks`}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors font-medium"
              >
                <Download className="w-4 h-4" />
                <span>Export for QuickBooks (.iif)</span>
              </a>
              <a
                href={`${API}/export/xero`}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors font-medium"
              >
                <Download className="w-4 h-4" />
                <span>Export for Xero (.csv)</span>
              </a>
            </div>
          </div>

          {/* Team Management Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-5 h-5 text-indigo-700" />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Team Collaboration</h2>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Create teams and invite members to collaborate on invoices
            </p>

            <TeamManagement />
          </div>

          {/* Google Drive Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Cloud className="w-5 h-5 text-indigo-700" />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Google Drive</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {driveConnected ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                        <p className="text-sm font-medium text-slate-900">Connected</p>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-slate-400" />
                        <p className="text-sm font-medium text-slate-900">Not Connected</p>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    {driveConnected
                      ? 'Your invoices are automatically backed up to Google Drive'
                      : 'Connect Google Drive to automatically backup your invoice Excel files'}
                  </p>
                </div>

                <div>
                  {driveConnected ? (
                    <button
                      onClick={disconnectDrive}
                      className="px-4 py-2 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all font-medium rounded-lg text-sm"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={connectDrive}
                      disabled={isConnecting}
                      className="px-4 py-2 bg-indigo-700 text-white hover:bg-indigo-800 shadow-sm hover:shadow-md transition-all font-medium rounded-lg text-sm flex items-center space-x-2 disabled:opacity-50"
                      data-testid="connect-drive-settings"
                    >
                      {isConnecting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <Cloud className="w-4 h-4" />
                          <span>Connect Drive</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {!driveConnected && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">Google Drive Integration Setup</p>
                      <p className="text-xs text-blue-800 mt-1 mb-3">
                        Google Drive integration requires OAuth configuration in Google Cloud Console. This is an optional feature - you can download Excel files locally without Drive.
                      </p>
                      
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium text-blue-900 hover:text-blue-700">
                          Setup Instructions (for administrators)
                        </summary>
                        <ol className="list-decimal list-inside mt-2 space-y-1 text-blue-800 ml-2">
                          <li>Create project in <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                          <li>Enable Google Drive API</li>
                          <li>Create OAuth 2.0 Web Application credentials</li>
                          <li>Add authorized redirect URI: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/drive-callback</code></li>
                          <li>Add JavaScript origin: <code className="bg-blue-100 px-1 rounded">{window.location.origin}</code></li>
                          <li>Add Client ID and Secret to app configuration</li>
                        </ol>
                      </details>

                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs text-blue-800 font-medium">Current Status:</p>
                        <p className="text-xs text-blue-700 mt-1">
                          ✓ Local Excel download: <span className="font-semibold">Available</span><br/>
                          ✗ Google Drive backup: <span className="font-semibold">Requires OAuth setup</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Privacy Section */}
          <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-5 h-5 text-indigo-700" />
              <h2 className="text-xl font-heading font-semibold text-slate-900">Privacy & Security</h2>
            </div>

            <div className="space-y-4 text-sm text-slate-600">
              <div className="flex items-start space-x-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Two-Factor Authentication (2FA)</p>
                  <p className="text-xs mt-0.5 text-emerald-700">Email OTP verification enabled for all logins</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Invite-Only Access</p>
                  <p className="text-xs mt-0.5 text-emerald-700">Only invited email addresses can join this app</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">End-to-End Encryption</p>
                  <p className="text-xs mt-0.5">All data is encrypted in transit and at rest</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Secure Authentication</p>
                  <p className="text-xs mt-0.5">Google OAuth 2.0 with secure session management</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-indigo-700 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
            >
              ← Back to Dashboard
            </button>
          </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}

export default SettingsPage;
